import { useState, useRef, useCallback } from "react";
import { UploadSimple, X, File as FileIcon, Microphone, PushPin } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const ALLOWED_EXTENSIONS = [
  "pdf", "doc", "docx", "xlsx", "xls", "csv",
  "png", "jpg", "jpeg", "gif", "webp",
  "txt", "md",
  "mp3", "mp4", "m4a", "wav", "webm", "ogg",
];

const AUDIO_EXTENSIONS = ["mp3", "mp4", "m4a", "wav", "webm", "ogg"];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileUploadZone = ({ files, onFilesChange, uploadedFileIds = [], onFileIdsChange }) => {
  const { api } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [pinning, setPinning] = useState({});
  const [pinned, setPinned] = useState({});
  const fileInputRef = useRef(null);

  const handleFiles = useCallback((newFiles) => {
    const validFiles = newFiles.filter((f) => {
      const ext = f.name.split(".").pop().toLowerCase();
      return ALLOWED_EXTENSIONS.includes(ext);
    });
    onFilesChange([...files, ...validFiles]);
  }, [files, onFilesChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [handleFiles]);

  const removeFile = (idx) => {
    onFilesChange(files.filter((_, i) => i !== idx));
  };

  const isAudio = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    return AUDIO_EXTENSIONS.includes(ext);
  };

  const pinToResourceBank = async (file, idx) => {
    const key = `${file.name}-${idx}`;
    setPinning((p) => ({ ...p, [key]: true }));
    try {
      // Upload file first to get file_id
      const formData = new FormData();
      formData.append("file", file);
      const uploadResp = await api.post("/upload", formData);
      const fileId = uploadResp.data.file_id;

      // Add to resource bank with always_include = true
      await api.post("/resource-bank", {
        file_id: fileId,
        display_name: file.name,
        always_include: true,
        tags: [],
        description: "",
      });
      setPinned((p) => ({ ...p, [key]: true }));
      toast.success(`"${file.name}" pinned to Resource Bank`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to pin file";
      if (detail.includes("already in")) {
        setPinned((p) => ({ ...p, [key]: true }));
        toast.info("File is already in your Resource Bank");
      } else {
        toast.error(detail);
      }
    } finally {
      setPinning((p) => ({ ...p, [key]: false }));
    }
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative cursor-pointer rounded-md border-2 border-dashed p-6 text-center transition-all duration-200 ${
          isDragging
            ? "border-[#FF4400] bg-[#FF4400]/5 shadow-[0_0_20px_rgba(255,68,0,0.15)]"
            : "border-[var(--border-strong)] hover:border-[#FF4400]/30 hover:bg-[var(--bg-raised)]/50"
        }`}
        data-testid="file-upload-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.gif,.webp,.txt,.md,.mp3,.mp4,.m4a,.wav,.webm,.ogg"
          onChange={(e) => { handleFiles(Array.from(e.target.files)); e.target.value = ""; }}
          className="hidden"
          data-testid="file-upload-input"
        />
        <UploadSimple size={24} className="mx-auto mb-2 text-[var(--text-muted)]" />
        <p className="text-sm text-[var(--text-secondary)]">Drop files here or click to browse</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          PDF, Word, Excel, CSV, Images, Text, Audio (mp3, m4a, wav) — max 25MB
        </p>
      </div>

      <AnimatePresence>
        {files.map((file, idx) => {
          const audio = isAudio(file.name);

          return (
            <motion.div
              key={`${file.name}-${idx}-${file.size}`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className={`flex items-center justify-between px-3 py-2 rounded-md border ${
                audio ? "bg-[var(--bg-raised)] border-[#FF4400]/20" : "bg-[var(--bg-raised)] border-[var(--border)]"
              }`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {audio ? (
                    <Microphone size={16} className="text-[#FF4400] shrink-0" />
                  ) : (
                    <FileIcon size={16} className="text-[#FF4400] shrink-0" />
                  )}
                  <span className="text-sm text-[var(--text-secondary)] truncate">{file.name}</span>
                  <span className="text-xs text-[var(--text-muted)] shrink-0">({formatFileSize(file.size)})</span>
                </div>

                <div className="flex items-center gap-1 shrink-0 ml-2">
                  {!pinned[`${file.name}-${idx}`] ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); pinToResourceBank(file, idx); }}
                      disabled={pinning[`${file.name}-${idx}`]}
                      className="p-1 rounded hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--success)] transition-colors disabled:opacity-40"
                      title="Pin to Resource Bank (always include)"
                      data-testid={`pin-file-${idx}`}
                    >
                      <PushPin size={14} className={pinning[`${file.name}-${idx}`] ? "animate-pulse" : ""} />
                    </button>
                  ) : (
                    <span className="p-1 text-[var(--success)]" title="Pinned to Resource Bank" data-testid={`pinned-file-${idx}`}>
                      <PushPin size={14} weight="fill" />
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                    className="p-1 rounded hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[#FF4400] transition-colors"
                    data-testid={`remove-file-${idx}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
